import { useCallback, useMemo } from 'react';
import { NodeWithoutDescription } from '../../../../../../api/table-of-content';
import type { Result, ResultsForScorableItem } from '../../../../../../utils/results';
import { findDeepNodeById } from '../../../../../../utils/tocNode';
import { ContentType } from '../../../../components/MaterialIcon';
import { ScorableItem } from '../../../api/scorable-items';
import { ResultsScore } from '../../../api/results';
import { RequiredFields } from '../../../../../../@types/utils';
import { FilteredItem } from '../../scorable-item-filter-modal/ScorableItemFilterModal';
import groupBy from 'lodash/groupBy';
// eslint-disable-next-line no-restricted-imports
import type { Dictionary } from 'lodash';
import { Module } from '../../../../../../api/module';
import { useResultsDataContext } from '../../../context/ResultsDataContext';
import { useFilters } from '../../../context/filter-context/FilterContext';

type RowHeaderLevel = 0 | 1 | 2 | 3;
function isRowHeaderLevel(level: number): level is RowHeaderLevel {
  return level >= 0 && level <= 3;
}

interface ScorableItemRowHeaderBase {
  // concatenation (with ";") of materialId and scorableItemType
  id: string;
  nodeId: string;
  materialId: string;
  materialType: 'medialink' | 'user-material';
  contentType?: ContentType;
  title: string;
  scorableItemType: 'free-exercise' | 'assignment';
  level: RowHeaderLevel;
  unachievable: boolean;
  showPoints: boolean;
}

interface AssignmentScorableItemRowHeader extends ScorableItemRowHeaderBase {
  scorableItemType: 'assignment';
  assignmentStartDate: Date;
  assignmentEndDate: Date;
}

interface FreeExerciseScorableItemRowHeader extends ScorableItemRowHeaderBase {
  scorableItemType: 'free-exercise';
}

export type ScorableItemRowHeader = AssignmentScorableItemRowHeader | FreeExerciseScorableItemRowHeader;

export interface TocNodeRowHeader {
  id: string;
  title: string;
  prefix?: string;
  imageUrl?: string;
  scorableItems?: ScorableItemRowHeader[];
  nodes?: TocNodeRowHeader[];
  parentNodeId: string | undefined;
  level: RowHeaderLevel;
}

interface AggregatedResult extends ResultsScore, Pick<RequiredFields<Result, 'duration'>, 'duration'> {
  pending: number;
}

interface UseTableData {
  allScorableItemsColumn: TocNodeRowHeader[];
  filteredScorableItemsColumn: TocNodeRowHeader[];
  hasResults: boolean;
  getResultForScorableMaterial: (
    memberId: string,
    scorableItemId: string,
    type: ScorableItemRowHeader['scorableItemType'],
  ) => ResultsForScorableItem | null;
  getResultForTocNode: (memberId: string, nodeId: string) => AggregatedResult;
}

function createScorableItemRowHeader(
  node: NodeWithoutDescription,
  scorableItem: ScorableItem,
  results: Result[],
  level: RowHeaderLevel,
): ScorableItemRowHeader[] {
  const resultsOfScorableItem = results.filter(r => r.exerciseId === scorableItem.id);
  const resultOfLastAssignment = resultsOfScorableItem
    .sort((r1, r2) => {
      const date1 = new Date(r1.achievedAt || '');
      const date2 = new Date(r2.achievedAt || '');
      return date2.getTime() - date1.getTime();
    })
    .find(r => r.isAssignment);

  const resultsAreForFreeExercises = resultsOfScorableItem.some(r => !r.isAssignment);

  const rowHeaders: ScorableItemRowHeader[] = [];
  if (resultOfLastAssignment) {
    rowHeaders.push({
      id: `${scorableItem.id}-true`,
      nodeId: node.id,
      materialId: scorableItem.id,
      materialType: scorableItem.type,
      title: scorableItem.name,
      scorableItemType: 'assignment',
      assignmentStartDate: new Date(resultOfLastAssignment.assignmentStartDate),
      assignmentEndDate: new Date(resultOfLastAssignment.assignmentEndDate),
      contentType: scorableItem.contentType,
      level,
      unachievable: scorableItem.unachievable,
      showPoints: scorableItem.showPoints,
    });
  }
  if (resultsAreForFreeExercises) {
    rowHeaders.push({
      id: `${scorableItem.id}-false`,
      nodeId: node.id,
      materialId: scorableItem.id,
      materialType: scorableItem.type,
      title: scorableItem.name,
      scorableItemType: 'free-exercise',
      contentType: scorableItem.contentType,
      level,
      unachievable: scorableItem.unachievable,
      showPoints: scorableItem.showPoints,
    });
  }
  return rowHeaders;
}

function shouldNodeRender(node: TocNodeRowHeader): boolean {
  function calculateExerciseTotalChildren(node: TocNodeRowHeader): number {
    if (!node.nodes) return node.scorableItems?.length || 0;

    return node.nodes.reduce((acc, childNode) => {
      return acc + calculateExerciseTotalChildren(childNode);
    }, node.scorableItems?.length || 0);
  }

  return calculateExerciseTotalChildren(node) !== 0;
}

interface MapNodeData {
  node: NodeWithoutDescription;
  scorableItems: ScorableItem[];
  results: Result[];
  level?: RowHeaderLevel;
  parentNode?: NodeWithoutDescription;
  filteredScorableItemsById?: Dictionary<FilteredItem[]>;
  filteredNodesById?: Dictionary<FilteredItem[]>;
  module: Module;
}

function mapNodeToRowHeader({
  node,
  scorableItems,
  results,
  level = 0,
  parentNode,
  filteredNodesById,
  filteredScorableItemsById,
  module,
}: MapNodeData): TocNodeRowHeader | null {
  const nextLevel = level + 1;

  if (filteredNodesById && !filteredNodesById[node.id]) return null;

  const scorableItemsToRender = filteredScorableItemsById
    ? scorableItems
        .filter(item => item.node === node.id)
        .flatMap(item => createScorableItemRowHeader(node, item, results, nextLevel as RowHeaderLevel))
        .filter(item => filteredScorableItemsById[item.id])
    : scorableItems
        .filter(item => item.node === node.id)
        .flatMap(item => createScorableItemRowHeader(node, item, results, nextLevel as RowHeaderLevel));

  return {
    id: node.id,
    title: node.title,
    prefix: node.displayPrefix ? node.prefix : undefined,
    imageUrl: node.id === 'general' ? module.styling?.logo : node.image,
    level,
    parentNodeId: parentNode?.id,
    nodes:
      node.nodes && isRowHeaderLevel(nextLevel)
        ? node.nodes
            ?.map(childNode =>
              mapNodeToRowHeader({
                node: childNode,
                scorableItems,
                results,
                level: nextLevel,
                parentNode: node,
                filteredNodesById,
                filteredScorableItemsById,
                module,
              }),
            )
            .filter((node): node is TocNodeRowHeader => node !== null)
            .filter(shouldNodeRender)
        : undefined,
    scorableItems: scorableItemsToRender,
  };
}

function flattenNodeIds(node: TocNodeRowHeader): string[] {
  const ids: string[] = [node.id];
  if (node.nodes) {
    node.nodes.forEach(childNode => {
      ids.push(...flattenNodeIds(childNode));
    });
  }
  return ids;
}

type Opts = {
  results: Result[];
  toc: NodeWithoutDescription[];
};

export default function useTableData({ results, toc }: Opts): UseTableData {
  const { scorableItemsPerSchoolYearPerModule } = useResultsDataContext();
  const { selectedSchoolYear, selectedModule, selectedTableItems, selectedMembers, selectedGroups } = useFilters();

  const scorableItems = useMemo(
    () => scorableItemsPerSchoolYearPerModule[selectedSchoolYear?.id || '']?.[selectedModule?.id || ''] || [],
    [scorableItemsPerSchoolYearPerModule, selectedModule?.id, selectedSchoolYear?.id],
  );

  const filteredScorableItemsById = useMemo(() => {
    return groupBy(
      selectedTableItems.filter(item => item.type === 'scorable-item'),
      'id',
    );
  }, [selectedTableItems]);

  const filteredNodesById = useMemo(() => {
    return groupBy(
      selectedTableItems.filter(item => item.type === 'node'),
      'id',
    );
  }, [selectedTableItems]);

  const allScorableItemsColumn = useMemo<UseTableData['allScorableItemsColumn']>(() => {
    if (!selectedModule) return [];

    return toc
      .map(node => mapNodeToRowHeader({ node, scorableItems, results, module: selectedModule }))
      .filter((node): node is TocNodeRowHeader => node !== null)
      .filter(shouldNodeRender);
  }, [selectedModule, toc, scorableItems, results]);

  const flattenScorableItems = useCallback((scorableItemsColumn: TocNodeRowHeader[]) => {
    function flattenScorableItemsInTree(
      node: TocNodeRowHeader,
      flattenedScorableItems: ScorableItemRowHeader[] = [],
    ): ScorableItemRowHeader[] {
      flattenedScorableItems.push(...(node.scorableItems || []));

      (node.nodes || []).forEach(childNode => {
        flattenScorableItemsInTree(childNode, flattenedScorableItems);
      });

      return flattenedScorableItems;
    }

    return scorableItemsColumn.flatMap(node => flattenScorableItemsInTree(node));
  }, []);

  const filteredScorableItemsColumn = useMemo<UseTableData['filteredScorableItemsColumn']>(() => {
    if (!Object.keys(filteredNodesById).length || !selectedModule) return allScorableItemsColumn;

    return toc
      .map(node =>
        mapNodeToRowHeader({
          node,
          scorableItems,
          results,
          filteredNodesById,
          filteredScorableItemsById,
          module: selectedModule,
        }),
      )
      .filter((node): node is TocNodeRowHeader => node !== null)
      .filter(shouldNodeRender);
  }, [
    filteredNodesById,
    selectedModule,
    allScorableItemsColumn,
    toc,
    scorableItems,
    results,
    filteredScorableItemsById,
  ]);

  const getResultForScorableMaterial = useCallback(
    (
      memberId: string,
      scorableItemId: string,
      type: ScorableItemRowHeader['scorableItemType'],
    ): ResultsForScorableItem | null => {
      const result = results.find(
        result =>
          result.memberId === memberId &&
          result.exerciseId === scorableItemId &&
          result.isAssignment === (type === 'assignment'),
      );

      if (!result) {
        return null;
      }

      if (result.needsGrading) {
        return {
          needsGrading: true,
          score: {
            achievable: 0,
            achieved: 0,
            maximum: 0,
          },
          session: result.session,
          completedBySystem: false,
          isZeroScore: false,
          achievedAt: result.achievedAt,
        };
      }

      return { ...result, score: { ...result.score, achieved: Math.max(result.score.achieved, 0) } };
    },
    [results],
  );

  const resultsCount = useMemo(() => {
    const tree = selectedTableItems.length ? filteredScorableItemsColumn : allScorableItemsColumn;

    const flattenedScorableItems = flattenScorableItems(tree);

    const members = selectedMembers[selectedGroups[0]?.id || ''] || [];

    let count = 0;

    for (const member of members) {
      for (const scorableItem of flattenedScorableItems) {
        const result = results.find(
          result =>
            result.memberId === member.id &&
            result.exerciseId === scorableItem.materialId &&
            result.isAssignment === (scorableItem.scorableItemType === 'assignment'),
        );

        if (result) count++;
      }
    }

    return count;
  }, [
    selectedTableItems.length,
    filteredScorableItemsColumn,
    allScorableItemsColumn,
    flattenScorableItems,
    selectedMembers,
    selectedGroups,
    results,
  ]);

  const aggregateScoreForNode = useCallback(
    (node: TocNodeRowHeader, results: Result[]): Omit<AggregatedResult, 'hasShowPoints'> => {
      const score: Omit<AggregatedResult, 'hasShowPoints'> = {
        achievable: 0,
        achieved: 0,
        maximum: 0,
        pending: 0,
        duration: 0,
      };

      results.reduce((acc, result) => {
        const scorableItemId = `${result.exerciseId}-${result.isAssignment ? 'true' : 'false'}`;

        const scorableItem = (node.scorableItems || []).find(item => item.id === scorableItemId);
        if (!scorableItem || scorableItem.unachievable) {
          return acc;
        }

        if (result.needsGrading) {
          acc.pending += result.score.maximum;
        } else {
          acc.achievable += result.score.achievable;
          acc.achieved += result.score.achieved;
        }

        acc.maximum += result.score.maximum;
        acc.duration += result.duration || 0;

        return acc;
      }, score);

      if (!node.nodes?.length) return score;

      node.nodes.forEach(childNode => {
        const childScore = aggregateScoreForNode(childNode, results);
        score.achievable += childScore.achievable;
        score.achieved += childScore.achieved;
        score.pending += childScore.pending;
        score.maximum += childScore.maximum;
        score.duration += childScore.duration;
      });

      return score;
    },
    [],
  );

  const getResultForTocNode = useCallback(
    (memberId: string, nodeId: string): AggregatedResult => {
      const node = findDeepNodeById(filteredScorableItemsColumn, nodeId);

      if (!node) {
        return {
          achievable: 0,
          achieved: 0,
          maximum: 0,
          pending: 0,
          duration: 0,
        };
      }

      const nodeIds = flattenNodeIds(node);

      return aggregateScoreForNode(
        node,
        results.filter(result => result.memberId === memberId && nodeIds.includes(result.nodeId)),
      );
    },
    [aggregateScoreForNode, filteredScorableItemsColumn, results],
  );

  return {
    hasResults: Boolean(resultsCount),
    allScorableItemsColumn,
    filteredScorableItemsColumn,
    getResultForScorableMaterial,
    getResultForTocNode,
  };
}
